/**
 * Vacancy store with quota-aware FIFO for local mode
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { QuotaManager } from '@/services/quota/QuotaManager';
import { useAuthStore } from './auth.store';
import type { Vacancy } from '@/services/vacancy.service';

interface VacancyState {
  vacancies: Vacancy[];
  selectedVacancy: Vacancy | null;
  isLoading: boolean;
}

interface VacancyActions {
  addVacancy: (vacancy: Vacancy) => { success: boolean; removed?: Vacancy };
  removeVacancy: (id: string) => void;
  selectVacancy: (vacancy: Vacancy | null) => void;
  setLoading: (loading: boolean) => void;
  clearVacancies: () => void;
  _hydrate: () => void;
}

export type VacancyStore = VacancyState & VacancyActions;

export const useVacancyStore = create<VacancyStore>()(
  persist(
    (set) => ({
      // State
      vacancies: [],
      selectedVacancy: null,
      isLoading: false,

      // Actions

      /**
       * Add vacancy with FIFO enforcement.
       * Returns { success: true, removed?: Vacancy } if added.
       */
      addVacancy: (vacancy) => {
        const { mode } = useAuthStore.getState();

        if (mode === 'local') {
          // Use QuotaManager for local mode (FIFO)
          const result = QuotaManager.addVacancy(vacancy);

          // Sync Zustand state with QuotaManager
          const savedVacancies = QuotaManager.getSavedVacancies();
          set({ vacancies: savedVacancies });

          return { success: true, removed: result.removed };
        } else {
          // Cloud mode: no limits, just add
          set((s) => ({ vacancies: [...s.vacancies, vacancy] }));
          return { success: true };
        }
      },

      removeVacancy: (id) => {
        const { mode } = useAuthStore.getState();

        if (mode === 'local') {
          QuotaManager.removeVacancy(id);
          const savedVacancies = QuotaManager.getSavedVacancies();
          set({ vacancies: savedVacancies });
        } else {
          set((s) => ({ vacancies: s.vacancies.filter((v) => v.id !== id) }));
        }
      },

      selectVacancy: (vacancy) => {
        set({ selectedVacancy: vacancy });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      clearVacancies: () => {
        set({ vacancies: [], selectedVacancy: null });
      },

      // On hydration, sync with QuotaManager
      _hydrate: () => {
        const { mode } = useAuthStore.getState();
        if (mode === 'local') {
          const savedVacancies = QuotaManager.getSavedVacancies();
          set({ vacancies: savedVacancies });
        }
      },
    }),
    {
      name: 'vacancy-storage',
      partialize: (state) => ({
        vacancies: state.vacancies,
        selectedVacancy: state.selectedVacancy,
      }),
      onRehydrateStorage: () => (state) => {
        state?._hydrate();
      },
    }
  )
);
